import React, { useState } from 'react';
import {
  CubeIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import type { Package } from '../../hooks/usePackages';
import { usePackages } from '../../hooks/usePackages';
import SellPackageModal from './SellPackageModal';

interface PackageCardProps {
  pkg: Package;
  onEdit: (pkg: Package) => void;
}

export default function PackageCard({ pkg, onEdit }: PackageCardProps) {
  const { deletePackage, toggleActive, getAvailableQty, availability } = usePackages();
  const [showMenu, setShowMenu] = useState(false);
  const [showSell, setShowSell] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const avail = availability[pkg.id];
  const availQty = getAvailableQty(pkg);
  const hasLowStock = avail?.has_low_stock ?? false;
  const allUnlimited = (pkg.items ?? []).every((i) => i.product?.inventory_type === 'unlimited');

  const stockLabel = allUnlimited
    ? 'Siempre disponible'
    : availQty === 0
    ? 'Sin stock'
    : `${availQty} disponibles`;

  const stockColor = allUnlimited
    ? 'text-blue-600 bg-blue-50'
    : availQty === 0
    ? 'text-red-600 bg-red-50'
    : hasLowStock
    ? 'text-amber-600 bg-amber-50'
    : 'text-green-600 bg-green-50';

  return (
    <>
      <div
        className={`bg-white rounded-xl border ${
          pkg.is_active ? 'border-gray-100' : 'border-gray-100 opacity-60'
        } shadow-sm hover:shadow-md transition-all`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                <CubeIcon className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 leading-tight">{pkg.name}</h3>
                {pkg.description && (
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{pkg.description}</p>
                )}
              </div>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"
              >
                <EllipsisVerticalIcon className="w-4 h-4" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} aria-hidden="true" />
                  <div className="absolute right-0 top-8 z-20 bg-white border border-gray-100 rounded-xl shadow-lg py-1 w-44">
                    <button
                      type="button"
                      onClick={() => {
                        onEdit(pkg);
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <PencilIcon className="w-4 h-4" /> Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        toggleActive(pkg.id, pkg.is_active);
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {pkg.is_active ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmDelete(true);
                        setShowMenu(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50"
                    >
                      <TrashIcon className="w-4 h-4" /> Eliminar
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          {pkg.price != null && (
            <div className="text-xl font-bold text-gray-900 mb-3">
              ${Number(pkg.price).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
            </div>
          )}
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${stockColor}`}
          >
            {allUnlimited ? (
              <span aria-hidden>∞</span>
            ) : availQty === 0 ? (
              <ExclamationTriangleIcon className="w-4 h-4" />
            ) : hasLowStock ? (
              <ExclamationTriangleIcon className="w-4 h-4" />
            ) : (
              <CheckCircleIcon className="w-4 h-4" />
            )}
            {stockLabel}
          </span>
        </div>
        <div className="px-4 pb-3">
          <p className="text-xs text-gray-400 mb-2 font-medium">CONTENIDO</p>
          <div className="space-y-1.5">
            {(pkg.items ?? []).map((item) => {
              const isUnlimited = item.product?.inventory_type === 'unlimited';
              const stock = item.product?.stock_quantity ?? 0;
              const needed = item.quantity;
              const lowStock = !isUnlimited && stock < needed;
              return (
                <div key={item.product_id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 flex items-center gap-1.5">
                    {isUnlimited && <span className="text-blue-400">∞</span>}
                    {lowStock && <ExclamationTriangleIcon className="w-4 h-4 text-amber-400" />}
                    {item.quantity % 1 === 0 ? item.quantity : Number(item.quantity).toFixed(2)}x{' '}
                    {item.product?.name}
                  </span>
                  {isUnlimited ? (
                    <span className="text-xs text-blue-500">
                      {item.unit_cost_override != null
                        ? `$${item.unit_cost_override}/u`
                        : 'sin costo'}
                    </span>
                  ) : (
                    <span
                      className={`text-xs ${lowStock ? 'text-amber-500 font-medium' : 'text-gray-400'}`}
                    >
                      {lowStock ? `solo ${stock}` : `${stock} en stock`}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {pkg.is_active && (
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={() => setShowSell(true)}
              className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white text-sm font-medium py-2 rounded-xl hover:bg-purple-700 transition-colors"
            >
              <ShoppingCartIcon className="w-4 h-4" />
              Vender paquete
            </button>
          </div>
        )}
      </div>

      {showSell && (
        <SellPackageModal pkg={pkg} onClose={() => setShowSell(false)} />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="font-semibold text-gray-900 mb-2">¿Eliminar paquete?</h3>
            <p className="text-sm text-gray-500 mb-5">
              Se eliminará &quot;{pkg.name}&quot; permanentemente. Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-2 text-sm border border-gray-200 rounded-xl hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  deletePackage(pkg.id);
                  setConfirmDelete(false);
                }}
                className="flex-1 py-2 text-sm bg-red-500 text-white rounded-xl hover:bg-red-600"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
